// PROMPT 149: respuestas rapidas del equipo (las comparte todo el equipo).
// - useRespuestasRapidas: el "/" del cuadro de escribir y el boton del rayo.
// - RespuestasRapidasDialog: lista para crear, editar y borrar (menu de la pagina).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { nombreCorto } from "@/lib/marcasWa";

const CAMPOS = /\{(nombre|marca|reserva|tour|fecha|hora)\}/g;
const EMPIEZA_CON_BARRA = /^\/[^\s]*$/;

// Reemplaza {nombre}, {marca}, {reserva}, {tour}, {fecha} y {hora} con los datos de la
// conversacion. Si falta un dato deja el {campo} a la vista para completarlo a mano.
export function rellenarCampos(texto, valores) {
  const datos = valores || {};
  return String(texto || "").replace(CAMPOS, (entero, campo) => {
    const v = datos[campo];
    return v === undefined || v === null || String(v).trim() === "" ? entero : String(v);
  });
}

export function useRespuestasRapidas({ marcaSlug, texto, setTexto, valores, area, activo }) {
  const [lista, setLista] = useState(null);
  const [manual, setManual] = useState(false);
  const [descartado, setDescartado] = useState(null);
  const [indice, setIndice] = useState(0);
  const textoAnterior = useRef(texto);

  const escrito = texto || "";
  const consulta = EMPIEZA_CON_BARRA.test(escrito) ? escrito.slice(1).toLowerCase() : null;
  const abierto = Boolean(activo) && descartado !== texto && (manual || consulta !== null);

  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    api
      .get("/whatsapp/respuestas-rapidas", { params: marcaSlug ? { marca_slug: marcaSlug } : {} })
      .then((r) => vivo && setLista(r.data || []))
      .catch(() => vivo && setLista([]));
    return () => {
      vivo = false;
    };
  }, [abierto, marcaSlug]);

  // Si se escribe algo mientras estaba abierta con el boton, se cierra sola.
  useEffect(() => {
    if (textoAnterior.current !== texto) {
      textoAnterior.current = texto;
      if (manual) setManual(false);
    }
  }, [texto, manual]);

  useEffect(() => {
    setIndice(0);
  }, [consulta, manual]);

  const filtradas = useMemo(() => {
    const q = consulta || "";
    return (lista || []).filter(
      (r) =>
        !q ||
        (r.atajo || "").includes(q) ||
        (r.titulo || "").toLowerCase().includes(q) ||
        (r.texto || "").toLowerCase().includes(q)
    );
  }, [lista, consulta]);

  const elegir = useCallback(
    (r) => {
      const base = rellenarCampos(r.texto, valores);
      if (consulta !== null) setTexto(base);
      else setTexto(escrito ? `${escrito}${/\s$/.test(escrito) ? "" : " "}${base}` : base);
      api.post(`/whatsapp/respuestas-rapidas/${encodeURIComponent(r.id)}/uso`).catch(() => {});
      setManual(false);
      setDescartado(null);
      requestAnimationFrame(() => {
        const campo = area && area.current;
        if (!campo) return;
        campo.focus();
        if (campo.setSelectionRange) campo.setSelectionRange(campo.value.length, campo.value.length);
      });
    },
    [consulta, escrito, valores, setTexto, area]
  );

  // Devuelve true si la tecla era para la lista (flechas, Enter, Tab y Esc).
  const alTeclear = useCallback(
    (e) => {
      if (!abierto) return false;
      const n = filtradas.length;
      if (e.key === "Escape") {
        e.preventDefault();
        setManual(false);
        setDescartado(texto);
        return true;
      }
      if (!n) return false;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndice((i) => (i + 1) % n);
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndice((i) => (i - 1 + n) % n);
        return true;
      }
      const enter = e.key === "Enter" && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing);
      if (enter || e.key === "Tab") {
        e.preventDefault();
        elegir(filtradas[Math.min(indice, n - 1)]);
        return true;
      }
      return false;
    },
    [abierto, filtradas, indice, texto, elegir]
  );

  const alternar = useCallback(() => {
    setDescartado(null);
    setManual((m) => !m);
    requestAnimationFrame(() => {
      if (area && area.current) area.current.focus();
    });
  }, [area]);

  const menu = abierto ? (
    <div
      className="absolute inset-x-3 bottom-full z-30 mb-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
      role="listbox"
      aria-label="Respuestas rápidas"
      data-testid="wa-rapidas-menu"
    >
      {lista === null ? (
        <p className="px-3 py-2 text-sm text-slate-500">Cargando…</p>
      ) : filtradas.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">
          {lista.length === 0
            ? "Todavía no hay respuestas rápidas. Se crean desde el menú de la lista → Respuestas rápidas."
            : "No hay ninguna con ese atajo."}
        </p>
      ) : (
        filtradas.map((r, i) => (
          <button
            key={r.id}
            type="button"
            role="option"
            aria-selected={i === indice}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => elegir(r)}
            onMouseEnter={() => setIndice(i)}
            data-testid={`wa-rapida-${r.atajo}`}
            className={`block w-full px-3 py-2 text-left ${i === indice ? "bg-slate-100" : "hover:bg-slate-50"}`}
          >
            <span className="flex min-w-0 items-center gap-2 text-sm">
              <span className="shrink-0 font-semibold text-slate-900">/{r.atajo}</span>
              <span className="truncate text-slate-600">{r.titulo}</span>
            </span>
            <span className="mt-0.5 block truncate text-xs text-slate-500">{r.texto}</span>
          </button>
        ))
      )}
    </div>
  ) : null;

  return { abierto, menu, alternar, alTeclear };
}

export function BotonRespuestasRapidas({ onClick, deshabilitado, activo }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={deshabilitado}
      aria-pressed={Boolean(activo)}
      title="Respuestas rápidas (también escribiendo /)"
      data-testid="wa-respuestas-rapidas"
      className={`mb-0.5 shrink-0 rounded-full p-2 hover:bg-black/5 disabled:opacity-40 ${
        activo ? "bg-black/5 text-slate-900" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      <Zap className="h-5 w-5" />
    </button>
  );
}

// ------------------------------------------------------------------ pantalla de gestion
const VACIA = { atajo: "", titulo: "", texto: "", marca_slug: "" };

export function RespuestasRapidasDialog({ open, onOpenChange, marcas }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(null); // null, "nueva" o el id
  const [form, setForm] = useState(VACIA);
  const [guardando, setGuardando] = useState(false);
  const [aBorrar, setABorrar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/whatsapp/respuestas-rapidas");
      setLista(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      cargar();
    } else {
      setEditando(null);
      setABorrar(null);
    }
  }, [open, cargar]);

  const nueva = () => {
    setForm(VACIA);
    setEditando("nueva");
  };
  const editar = (r) => {
    setForm({ atajo: r.atajo, titulo: r.titulo || "", texto: r.texto || "", marca_slug: r.marca_slug || "" });
    setEditando(r.id);
  };
  const cambiar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const guardar = async () => {
    const atajo = form.atajo.trim().toLowerCase();
    const cuerpo = {
      atajo,
      titulo: form.titulo.trim() || atajo,
      texto: form.texto.trim(),
      marca_slug: form.marca_slug || null,
    };
    setGuardando(true);
    try {
      if (editando === "nueva") await api.post("/whatsapp/respuestas-rapidas", cuerpo);
      else await api.put(`/whatsapp/respuestas-rapidas/${encodeURIComponent(editando)}`, cuerpo);
      toast.success("Respuesta rápida guardada.");
      setEditando(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (r) => {
    try {
      await api.delete(`/whatsapp/respuestas-rapidas/${encodeURIComponent(r.id)}`);
      toast.success("Respuesta rápida borrada.");
      setABorrar(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const puedeGuardar = form.atajo.trim() && form.texto.trim() && !guardando;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="wa-rapidas-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Respuestas rápidas</DialogTitle>
          <DialogDescription>
            Las comparte todo el equipo. En el chat se buscan escribiendo / o con el botón del rayo. Podés usar
            {" {nombre}, {marca}, {reserva}, {tour}, {fecha} y {hora}"}: se completan con los datos de la
            conversación y, si falta alguno, queda a la vista para completarlo a mano.
          </DialogDescription>
        </DialogHeader>

        {editando ? (
          <section className="space-y-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid="wa-rapida-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Atajo (minúsculas, números y _)</span>
                <Input
                  value={form.atajo}
                  maxLength={30}
                  onChange={(e) => cambiar("atajo", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                  placeholder="saludo"
                  data-testid="wa-rapida-atajo"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-slate-600">Marca</span>
                <select
                  value={form.marca_slug}
                  onChange={(e) => cambiar("marca_slug", e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800"
                  data-testid="wa-rapida-marca"
                >
                  <option value="">Todas</option>
                  {(marcas || []).map((m) => (
                    <option key={m.slug} value={m.slug}>
                      {nombreCorto(m.slug, marcas)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-600">Título (para reconocerla en la lista)</span>
              <Input
                value={form.titulo}
                maxLength={60}
                onChange={(e) => cambiar("titulo", e.target.value)}
                data-testid="wa-rapida-titulo"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-600">Texto</span>
              <textarea
                value={form.texto}
                maxLength={4096}
                rows={5}
                onChange={(e) => cambiar("texto", e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                data-testid="wa-rapida-texto"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditando(null)} disabled={guardando}>
                Cancelar
              </Button>
              <Button size="sm" onClick={guardar} disabled={!puedeGuardar} data-testid="wa-rapida-guardar">
                Guardar
              </Button>
            </div>
          </section>
        ) : (
          <div className="flex justify-end">
            <Button size="sm" onClick={nueva} className="gap-1.5" data-testid="wa-rapida-nueva">
              <Plus className="h-4 w-4" /> Nueva respuesta
            </Button>
          </div>
        )}

        <ul className="space-y-2" data-testid="wa-rapidas-lista">
          {cargando && lista.length === 0 ? <li className="text-sm text-slate-500">Cargando…</li> : null}
          {!cargando && lista.length === 0 ? (
            <li className="text-sm text-slate-500">Todavía no hay respuestas rápidas.</li>
          ) : null}
          {lista.map((r) => (
            <li key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`wa-rapida-fila-${r.atajo}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">/{r.atajo}</span>
                    <span className="truncate text-slate-700">{r.titulo}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                      {r.marca_slug ? nombreCorto(r.marca_slug, marcas) : "Todas"}
                    </span>
                    <span className="text-[11px] text-slate-400">{r.usos || 0} usos</span>
                  </p>
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-slate-600">{r.texto}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {aBorrar === r.id ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => borrar(r)} className="text-red-600" data-testid="wa-rapida-borrar-si">
                        Borrar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setABorrar(null)}>
                        No
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => editar(r)} aria-label="Editar" className="h-8 w-8">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setABorrar(r.id)}
                        aria-label="Borrar"
                        className="h-8 w-8"
                        data-testid={`wa-rapida-borrar-${r.atajo}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
